import React from "react";
import { Apple, Youtube } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { OFFICIAL_LINKS } from "@/lib/link";
import { SectionHead } from "@/components/SectionHead";
import { Reveal } from "@/components/Reveal";
import { formatPrice } from "@/lib/api";

function SpotifyGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M7 9.5c3.5-1 7-.5 10 1.2M7.5 13c3-.8 5.8-.4 8.3 1M8 16.2c2.4-.6 4.6-.3 6.6.8" />
    </svg>
  );
}

/** Featured release + full release list with official streaming destinations. */
export default function MusicSection({ full = false, showStreaming = true }) {
  const { releases, products } = useContent();
  const featured = releases[0];
  if (!featured) return null;

  const featuredRelated = products.filter((p) => !p.shop_hidden && featured.related_product_ids?.includes(p.id));
  const list = full ? releases : releases.slice(0, 3);

  const streamBtn = "h-16 px-8 border-2 border-[#2A2A36] hover:border-[#F26522] text-white font-display font-extrabold uppercase tracking-widest text-sm flex items-center justify-center gap-3 transition-colors active:scale-95";

  return (
    <div>
      <SectionHead eyebrow="Featured Release" title={`${featured.title} — Okaytwitchy`} index="/ 01">
        <p>Party at Red opens the CHILLWOAH sound — stream it on your platform of choice.</p>
      </SectionHead>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
        <Reveal className="lg:col-span-5">
          <div className="relative border border-[#1A1A22] bg-[#0F0F14]">
            <img src={featured.cover} alt={`Cover artwork for ${featured.title}`} loading="lazy" decoding="async" className="w-full aspect-square object-cover" />
            <div className="absolute inset-0" style={{ background: "radial-gradient(90% 60% at 50% 100%, rgba(242,101,34,0.18), transparent 60%)" }} aria-hidden="true" />
            <span className="absolute top-5 left-5 font-mono text-xs font-semibold tracking-[0.25em] uppercase text-[#F5E6D3] bg-[#08080A]/85 backdrop-blur px-3 py-1.5 border border-[#2A2A36]">
              {featured.type} • {featured.year}
            </span>
          </div>
        </Reveal>

        <div className="lg:col-span-7 flex flex-col gap-6">
          {showStreaming && (
            <Reveal delay={0.1}>
              <div className="flex flex-col gap-3">
                <button data-testid="listen-apple-music" onClick={() => window.open(OFFICIAL_LINKS.apple_music, "_blank", "noopener,noreferrer")} className={streamBtn}>
                  <Apple className="w-5 h-5" aria-hidden="true" /> Listen on Apple Music
                </button>
                <button data-testid="listen-spotify" onClick={() => window.open(OFFICIAL_LINKS.spotify, "_blank", "noopener,noreferrer")} className={streamBtn}>
                  <SpotifyGlyph /> Listen on Spotify
                </button>
                <button data-testid="listen-youtube" onClick={() => window.open(OFFICIAL_LINKS.youtube, "_blank", "noopener,noreferrer")} className={streamBtn}>
                  <Youtube className="w-5 h-5" aria-hidden="true" /> Watch — Listen on YouTube
                </button>
              </div>
            </Reveal>
          )}

          {featuredRelated.length > 0 && (
            <Reveal delay={0.2}>
              <div className="border border-[#1A1A22] bg-[#0F0F14] p-5 sm:p-6">
                <p className="font-mono text-xs font-semibold tracking-[0.25em] uppercase text-[#F26522] mb-4">
                  Worn with this sound — XRABI
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  {featuredRelated.map((p) => (
                    <a
                      key={p.id}
                      href="/shop"
                      data-testid={`related-product-${p.id}`}
                      className="flex items-center gap-4 border border-[#1A1A22] hover:border-[#F26522] p-3 transition-colors group flex-1"
                    >
                      <img src={p.images?.[0]?.url} alt="" loading="lazy" decoding="async" className="w-16 h-16 object-cover shrink-0" />
                      <div className="min-w-0">
                        <p className="font-display font-bold uppercase text-sm text-white truncate group-hover:text-[#F26522] transition-colors">
                          {p.name}
                        </p>
                        <p className="font-mono text-sm text-[#F26522] mt-1">{formatPrice(p.price, p.currency)}</p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </Reveal>
          )}
        </div>
      </div>

      <Reveal delay={0.1} className="mt-14">
        <ul data-testid="release-list" className="border-t border-[#1A1A22]">
          {list.map((r) => {
            return (
              <li key={r.id} className="border-b border-[#1A1A22]">
                <div className="flex items-center gap-4 sm:gap-6 py-5">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 shrink-0 relative border border-[#2A2A36]">
                    <img src={r.cover} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-display font-extrabold uppercase text-base sm:text-xl text-white truncate">{r.title}</p>
                    <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-300 mt-1">
                      {r.type} • {r.year}
                    </p>
                  </div>
                  {showStreaming && (
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        data-testid={`release-apple-${r.id}`}
                        onClick={() => window.open(r.apple_music_url || OFFICIAL_LINKS.apple_music, "_blank", "noopener,noreferrer")}
                        aria-label={`${r.title} on Apple Music`}
                        className="w-[52px] h-[52px] border border-[#2A2A36] hover:border-[#F26522] text-white hover:text-[#F26522] flex items-center justify-center transition-colors"
                      >
                        <Apple className="w-5 h-5" aria-hidden="true" />
                      </button>
                      <button
                        data-testid={`release-spotify-${r.id}`}
                        onClick={() => window.open(r.spotify_url || OFFICIAL_LINKS.spotify, "_blank", "noopener,noreferrer")}
                        aria-label={`${r.title} on Spotify`}
                        className="w-[52px] h-[52px] border border-[#2A2A36] hover:border-[#F26522] text-white hover:text-[#F26522] flex items-center justify-center transition-colors"
                      >
                        <SpotifyGlyph />
                      </button>
                      <button
                        data-testid={`release-youtube-${r.id}`}
                        onClick={() => window.open(r.youtube_url || OFFICIAL_LINKS.youtube, "_blank", "noopener,noreferrer")}
                        aria-label={`${r.title} on YouTube`}
                        className="w-[52px] h-[52px] border border-[#2A2A36] hover:border-[#F26522] text-white hover:text-[#F26522] flex items-center justify-center transition-colors"
                      >
                        <Youtube className="w-5 h-5" aria-hidden="true" />
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </Reveal>
    </div>
  );
}
